"use client";

import AdaAvailabilityNoticeModal from "@/components/common/modals/AdaAvailabilityNoticeModal";
import ActionButton from "@/components/library/buttons/ActionButton";
import { RateTypeAvailability } from "@/types/AvailabilityOption";
import { Unit } from "@/types/Unit";
import sanitize from "@/utils/sanitize";
import toPrice from "@/utils/toPrice";
import { useTranslations } from "next-intl";
import React, {
  ReactNode,
  useActionState,
  useCallback,
  useId,
  useState,
} from "react";
import { useFormStatus } from "react-dom";
import ButtonLink from "../buttons/ButtonLink";
import addBookingToCart from "./actions/addBookingToCart";
import "./index.scss";

const ReserveButton = ({ disabled }: { disabled: boolean }) => {
  const t = useTranslations("RateTypePicker");

  const data = useFormStatus();
  return (
    <ActionButton
      precedence="PRIMARY"
      size="LARGE"
      className="component reserve-button"
      type="submit"
      disabled={disabled || data.pending}
    >
      {t("reserve")}
    </ActionButton>
  );
};

// Also, this component should be refactored to work with progressive enhancement.
// If the form contains the details of the booking, we should be able to create it
// Also, the form should have an ENCRYPTED booking object, not a visible one that can be manipulated
export default function RateTypePicker({
  unit,
  rateTypeAvailabilities,
  bookableByPhoneOnly,
  hotelPhone,
  otherAdaUnitsAvailable = false,
  availabilitySearchUrl = "/rooms",
}: {
  unit: Unit;
  rateTypeAvailabilities: RateTypeAvailability[];
  bookableByPhoneOnly: boolean;
  hotelPhone: string;
  otherAdaUnitsAvailable?: boolean;
  availabilitySearchUrl?: string;
}) {
  const t = useTranslations("RateTypePicker");

  const id = useId();
  const [selectedRateTypeId, setSelectedRateTypeId] = useState(
    rateTypeAvailabilities.find(
      (rateTypeAvailabilility) =>
        rateTypeAvailabilility.restrictions.length === 0
    )?.rateType.id
  );
  const formRef = React.createRef<HTMLFormElement>();
  const [showAdaNoticeModal, setShowAdaNoticeModal] = useState(false);

  const selectedRateTypeAvailability = rateTypeAvailabilities.find(
    (rateTypeAvailability) =>
      rateTypeAvailability.rateType.id === selectedRateTypeId
  );

  const [state, formAction] = useActionState(
    (prevState: any, formData: FormData) => {
      return addBookingToCart(
        selectedRateTypeAvailability!.booking,
        prevState,
        formData
      );
    },
    {
      message: "",
    }
  );

  const validRateTypeNodes: ReactNode[] = [];
  const invalidRateTypeNodes: ReactNode[] = [];
  rateTypeAvailabilities.forEach((rateTypeAvailability) => {
    if (rateTypeAvailability.restrictions.length > 0) {
      // This rate is invalid
      invalidRateTypeNodes.push(
        <div
          className="rate-type invalid"
          key={rateTypeAvailability.rateType.id}
        >
          <div className="rate-type-header">
            <input
              type="radio"
              name="rate-type"
              value={rateTypeAvailability.rateType.id}
              checked={false}
              id={`${rateTypeAvailability.rateType.id}-${id}`}
              disabled
            />

            <div className="rate-type-name">
              {rateTypeAvailability.rateType.name}
            </div>
          </div>
          <div className="rate-type-body">
            {rateTypeAvailability.restrictions}
          </div>
        </div>
      );
    } else {
      const isChecked = selectedRateTypeId === rateTypeAvailability.rateType.id;
      const sanitizedRateTypeDescription =
        rateTypeAvailability.rateType &&
        rateTypeAvailability.rateType.description &&
        rateTypeAvailability.rateType.description.length > 0
          ? sanitize(
              rateTypeAvailability.rateType.description.replace(/\n/g, "<br>"),
              [
                "p",
                "a",
                "ul",
                "ol",
                "li",
                "b",
                "i",
                "u",
                "strong",
                "em",
                "strike",
                "br",
              ]
            )
          : null;

      const sanitizedPromotionDescription =
        rateTypeAvailability.appliedPromotion &&
        rateTypeAvailability.appliedPromotion.description &&
        rateTypeAvailability.appliedPromotion.description.length > 0
          ? sanitize(
              rateTypeAvailability.appliedPromotion.description.replace(
                /\n/g,
                "<br>"
              ),
              [
                "p",
                "a",
                "ul",
                "ol",
                "li",
                "b",
                "i",
                "u",
                "strong",
                "em",
                "strike",
                "br",
              ]
            )
          : null;
      validRateTypeNodes.push(
        <div
          className={`rate-type valid ${isChecked ? "selected" : ""}`}
          key={rateTypeAvailability.rateType.id}
          onClick={(e) => {
            setSelectedRateTypeId(rateTypeAvailability.rateType.id);
          }}
        >
          <div className="rate-type-header">
            <input
              type="radio"
              name="rate-type"
              value={rateTypeAvailability.rateType.id}
              checked={isChecked}
              onChange={() => {
                setSelectedRateTypeId(rateTypeAvailability.rateType.id);
              }}
              id={`${rateTypeAvailability.rateType.id}-${id}`}
            />

            <div className="rate-type-name">
              {rateTypeAvailability.rateType.name}
            </div>
            {!!rateTypeAvailability.appliedPromotion && (
              <div className="promotion-name">
                {rateTypeAvailability.appliedPromotion.name}
              </div>
            )}
            <div className="rate-type-price-container">
              {!!rateTypeAvailability.appliedPromotion && (
                <div
                  className="rate-type-price original-price"
                  aria-label="Price before promotion"
                >
                  <div className="rate-type-price-inner-wrapper">
                    <span className="price">
                      {toPrice(
                        rateTypeAvailability.averagePricePerDayBeforePromotion
                      ).replace(/\.00$/, "")}
                    </span>
                    <span className="per-night"> / night</span>
                  </div>
                </div>
              )}
              <div className="rate-type-price final-price" aria-label="Price">
                <span className="price">
                  {toPrice(rateTypeAvailability.averagePricePerDay).replace(
                    /\.00$/,
                    ""
                  )}
                </span>
                <span className="per-night"> / night</span>
              </div>
            </div>
          </div>
          {(sanitizedRateTypeDescription || sanitizedPromotionDescription) && (
            <div className="rate-type-body">
              {sanitizedRateTypeDescription ? (
                <div
                  className="rate-type-description"
                  dangerouslySetInnerHTML={{
                    __html: sanitizedRateTypeDescription,
                  }}
                />
              ) : null}

              {sanitizedPromotionDescription ? (
                <div
                  className={`promotion-description ${!sanitizedRateTypeDescription ? "no-description-above" : ""}`}
                >
                  {t("promotion-details")}:{" "}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: sanitizedPromotionDescription,
                    }}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      );
    }
  });

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      if (!showAdaNoticeModal && unit.adaCompliant && otherAdaUnitsAvailable) {
        e.preventDefault();
        setShowAdaNoticeModal(true);
        return;
      } else {
        setShowAdaNoticeModal(false);
      }

      if (selectedRateTypeAvailability?.rateType.is_member_rate) {
        const thinkTravelButtonEl: HTMLAnchorElement | null =
          document.querySelector(
            "header div.component.auth-button a.thinktravel-button"
          );
        const isLoggedIn =
          thinkTravelButtonEl && thinkTravelButtonEl.textContent !== "Sign In";

        if (isLoggedIn === false && thinkTravelButtonEl) {
          e.preventDefault();
          thinkTravelButtonEl.click();
        }
      }
    },
    [
      otherAdaUnitsAvailable,
      selectedRateTypeAvailability?.rateType.is_member_rate,
      showAdaNoticeModal,
      unit.adaCompliant,
    ]
  );

  return (
    <div className="component rate-type-picker">
      <form
        ref={formRef}
        action={selectedRateTypeAvailability ? formAction : undefined}
        onSubmit={handleFormSubmit}
      >
        <div className="rate-types">
          {validRateTypeNodes.length > 0
            ? validRateTypeNodes
            : invalidRateTypeNodes}
        </div>
        {bookableByPhoneOnly ? (
          <ButtonLink href={`tel:${hotelPhone}`} className="call-us-button">
            {t("call-us")}
          </ButtonLink>
        ) : (
          <ReserveButton disabled={validRateTypeNodes.length === 0} />
        )}
      </form>

      {showAdaNoticeModal && (
        <AdaAvailabilityNoticeModal
          availabilitySearchUrl={availabilitySearchUrl}
          onClose={() => setShowAdaNoticeModal(false)}
          onConfirm={() => {
            formRef.current?.requestSubmit();
          }}
        />
      )}
    </div>
  );
}
